import { groupInviteGetInviteDetails, groupInviteRespond } from '@/api/gen-new/group/group';
import { authMe } from '@/api/gen-new/auth/auth';
import { routeAfterAuthenticated } from '@/auth/constants/authSettings';
import useAuth from '@/auth/useAuth';
import SuspenseLoader from '@/components/loaders/SuspenseLoader';
import { Alert, Box, Button, Card, Stack, Text, Title } from '@mantine/core';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

export default function InvitationPage() {
	const { code } = useParams<{ code: string }>();
	const { isAuthenticated, getSession } = useAuth();

	if (!code) {
		return <InvalidInvitationView message={t('invitation.invalidCode')} isAuthenticated={isAuthenticated()} />;
	}

	return (
		<Box maw={400} mx="auto" mt="xl">
			<InvitationValidator code={code} isAuthenticated={isAuthenticated()} getSession={getSession} />
		</Box>
	);
}

function InvitationValidator({
	code,
	isAuthenticated,
	getSession,
}: {
	code: string;
	isAuthenticated: boolean;
	getSession: () => any;
}) {
	const navigate = useNavigate();
	const [isValidating, setIsValidating] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const validateAndAccept = async () => {
			try {
				// Fetch invite details (including email and user existence check)
				const details = await groupInviteGetInviteDetails(code);

				if (isAuthenticated) {
					// If user is authenticated, check if their email matches the invitation
					try {
						const user = await authMe();

						// Compare logged user's email with invitation email
						if (user.email.toLowerCase() !== details.email.toLowerCase()) {
							setError(
								t('invitation.userMismatch', {
									userEmail: user.email,
								}),
							);
							setIsValidating(false);
							return;
						}
					} catch {
						// If we can't get user details, show generic error
						setError(t('invitation.validationError'));
						setIsValidating(false);
						return;
					}

					// Accept invite if authenticated and email matches
					try {
						await groupInviteRespond(code, { accept: true });
						navigate(routeAfterAuthenticated);
					} catch (err: any) {
						if (err?.response?.status === 422 && err?.response?.data?.message === 'invite.already.accepted') {
							navigate(routeAfterAuthenticated);
							return;
						}
						setIsValidating(false);
						setError(t('invitation.notFound'));
					}
				} else {
					// Not authenticated
					if (details.userExists) {
						// Redirect to login with code (login page will fetch email from invitation)
						navigate(`/login?code=${code}`);
					} else {
						// Redirect to register with code (register page will fetch email from invitation)
						navigate(`/register?code=${code}`);
					}
				}
			} catch (err: any) {
				setIsValidating(false);
				if (err?.response?.status === 422) {
					const errorData = err.response.data;
					if (errorData?.message === 'invite.expired') {
						setError(t('invitation.expired'));
					} else if (errorData?.message === 'invite.not.found') {
						setError(t('invitation.notFound'));
					} else {
						setIsValidating(false);
						setError(t('invitation.notFound'));
					}
				} else {
					setError(t('invitation.notFound'));
				}
			}
		};
		validateAndAccept();
	}, [code, isAuthenticated, navigate, getSession]);

	if (isValidating) {
		return (
			<Card shadow="sm" padding="lg" radius="md" withBorder>
				<Stack align="center" gap="md">
					<Title order={2} ta="center" c="blue">
						{t('invitation.title')}
					</Title>
					<Text size="sm" c="dimmed" ta="center">
						{t('invitation.validating')}
					</Text>
					<SuspenseLoader>
						<Text size="xs" c="dimmed">
							{t('invitation.pleaseWait')}
						</Text>
					</SuspenseLoader>
				</Stack>
			</Card>
		);
	}

	if (error) {
		return <InvalidInvitationView message={error} isAuthenticated={isAuthenticated} />;
	}

	return null;
}

function InvalidInvitationView({ message, isAuthenticated }: { message: string; isAuthenticated: boolean }) {
	const navigate = useNavigate();

	const handleButtonClick = () => {
		if (isAuthenticated) {
			navigate(routeAfterAuthenticated);
		} else {
			navigate('/login');
		}
	};

	return (
		<Card shadow="sm" padding="lg" radius="md" withBorder>
			<Stack align="center">
				<Title order={2} ta="center">
					{t('invitation.invalidTitle')}
				</Title>
				<Alert color="red" ta="center">
					{message}
				</Alert>
				<Button variant="filled" onClick={handleButtonClick}>
					{isAuthenticated ? t('invitation.goToDashboard') : t('invitation.goToLogin')}
				</Button>
			</Stack>
		</Card>
	);
}
